import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getLocations, deleteLocation } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { PageHeader } from '../../components/ui/PageHeader'
import { EmptyState } from '../../components/ui/EmptyState'
import { ConfirmModal, useConfirm } from '../../components/ui/ConfirmModal'

export default function LocationList() {
  const qc = useQueryClient()
  const { confirm, close, state: confirmState } = useConfirm()
  const { data, isLoading } = useQuery({ queryKey: ['locations'], queryFn: getLocations })
  const deleteMut = useMutation({
    mutationFn: deleteLocation,
    onSuccess: (_, deletedId) => { qc.setQueryData(['locations'], old => (old ?? []).filter(l => l.id !== deletedId)); toast.success('Location deleted') },
    onError: (e) => toast.error(e.message),
  })

  return (
    <div>
      <PageHeader
        title="Locations"
        description="Cities and countries served"
        action={<Link to="/locations/new"><Button><i className="ti ti-plus" /> Add Location</Button></Link>}
      />

      <ConfirmModal open={confirmState.open} onClose={close} title={confirmState.title} message={confirmState.message} loading={deleteMut.isPending} onConfirm={() => { confirmState.onConfirm(); close() }} />

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      ) : !data?.length ? (
        <EmptyState
          icon="ti-map-pin"
          title="No locations yet"
          description="Add your first location to get started"
          action={<Link to="/locations/new"><Button size="sm">Add Location</Button></Link>}
        />
      ) : (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Flag</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">City</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Country</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Country Code</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">City Code</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map(loc => (
                <tr key={loc.id} className="hover:bg-gray-50/40 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="w-9 h-9 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-200">
                      {loc.flag && loc.flag.startsWith('http') ? (
                        <img src={loc.flag} alt={loc.countryLabel} className="w-full h-full object-cover" />
                      ) : loc.flag ? (
                        <span className="text-xl">{loc.flag}</span>
                      ) : (
                        <i className="ti ti-map-pin text-gray-400" />
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 font-medium text-gray-900">{loc.cityLabel}</td>
                  <td className="px-5 py-3.5 text-gray-600">{loc.countryLabel}</td>
                  <td className="px-5 py-3.5">
                    <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{loc.country}</code>
                  </td>
                  <td className="px-5 py-3.5">
                    <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{loc.city}</code>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <Button
                      variant="ghost" size="xs"
                      className="text-gray-400 hover:text-red-500"
                      onClick={() => confirm({ title: 'Delete Location', message: `Delete "${loc.cityLabel}"? This action cannot be undone.`, onConfirm: () => deleteMut.mutate(loc.id) })}
                    >
                      <i className="ti ti-trash" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
